import React from 'react';
import { PageSection } from '../../../types/pageBuilder';

interface SectionPreviewProps {
  section: PageSection;
}

/**
 * Visual preview of a section
 * Shows a simplified representation of the section
 */
const SectionPreview: React.FC<SectionPreviewProps> = ({ section }) => {
  const renderPreview = () => {
    // Get content from section if available
    const content = section.content?.en || section.content?.ar || {};

    switch (section.section_type) {
      case 'HeroSection':
        return (
          <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-2">
              {content.title?.value || 'Hero Title'}
            </h2>
            <p className="text-blue-100 mb-4">
              {content.subtitle?.value || 'Hero subtitle goes here...'}
            </p>
            <button className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium">
              {content.cta_text?.value || 'Call to Action'}
            </button>
          </div>
        );

      case 'WhyChooseUs':
      case 'WhyChooseUsAbout':
        return (
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-3">
              {content.title?.value || 'Why Choose Us'}
            </h3>
            <p className="text-gray-600 mb-4">
              {content.subtitle?.value || 'Section subtitle'}
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white p-3 rounded border border-gray-200">
                  <div className="w-8 h-8 bg-blue-100 rounded mb-2"></div>
                  <p className="text-xs font-medium">Feature {i}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'HowItWorks':
        return (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-xl font-semibold mb-4">
              {content.title?.value || 'How It Works'}
            </h3>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <React.Fragment key={i}>
                  <div className="flex-1 bg-blue-50 p-3 rounded text-center">
                    <div className="w-8 h-8 bg-blue-500 text-white rounded-full mx-auto mb-2 flex items-center justify-center text-sm font-bold">
                      {i}
                    </div>
                    <p className="text-xs">Step {i}</p>
                  </div>
                  {i < 4 && <span className="text-gray-400">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        );

      case 'ContactSection':
      case 'ContactForm':
        return (
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-3">
              {content.title?.value || 'Contact Us'}
            </h3>
            <p className="text-gray-600 mb-4">
              {content.description?.value || 'Get in touch with us'}
            </p>
            <div className="space-y-2">
              <div className="h-8 bg-white border border-gray-200 rounded"></div>
              <div className="h-8 bg-white border border-gray-200 rounded"></div>
              <div className="h-16 bg-white border border-gray-200 rounded"></div>
              <button className="bg-blue-600 text-white px-4 py-2 rounded text-sm">
                Send Message
              </button>
            </div>
          </div>
        );

      case 'Statistics':
        return (
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold mb-4">Statistics</h3>
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold text-blue-600">100+</div>
                  <p className="text-xs text-gray-600">Stat {i}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'ServicesSummary':
        return (
          <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-center text-gray-500">
              <div className="text-4xl mb-2">🌍</div>
              <h3 className="font-semibold">Services Summary</h3>
              <p className="text-xs mt-1">Dynamic content from database</p>
              <p className="text-xs text-gray-400">(Not editable)</p>
            </div>
          </div>
        );

      case 'EnterprisePackagesSection':
        return (
          <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-center text-gray-500">
              <div className="text-4xl mb-2">📦</div>
              <h3 className="font-semibold">Packages</h3>
              <p className="text-xs mt-1">Dynamic content from database</p>
              <p className="text-xs text-gray-400">(Not editable)</p>
            </div>
          </div>
        );

      case 'FAQ':
        return (
          <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-center text-gray-500">
              <div className="text-4xl mb-2">❓</div>
              <h3 className="font-semibold">FAQ</h3>
              <p className="text-xs mt-1">Dynamic content from site_content table</p>
              <p className="text-xs text-gray-400">(Not editable)</p>
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-gray-100 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-700">
              {section.section_type}
            </h3>
            <p className="text-sm text-gray-500 mt-2">Section Key: {section.section_key}</p>
            {section.config?.editable === false && (
              <p className="text-xs text-gray-400 mt-2">Dynamic content (not editable)</p>
            )}
          </div>
        );
    }
  };

  return (
    <div className={`relative ${!section.is_visible ? 'opacity-50' : ''}`}>
      {renderPreview()}
      {!section.is_visible && (
        <div className="absolute top-2 right-2 bg-gray-900/75 text-white px-2 py-1 rounded text-xs">
          Hidden
        </div>
      )}
    </div>
  );
};

export default SectionPreview;
